/**
 * The Checklist tab: the form the inspection's type names, filled on site.
 * The Safety check tab is the same, on the type's safety check, with what
 * holds the start above it.
 *
 * Shows the form and the version it asks, how far it has got, the live score
 * when its answers carry one, what the tenant's rules found and why, and
 * each section with what it still needs. A
 * section opens as a form. Its answers save with the section version read
 * when it opened, so a save never overwrites one made meanwhile, and moving
 * to another section saves first. Once the result is recorded the checklist
 * is locked and says so. Everything is read from the checklist API; nothing
 * here knows any tenant's form.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  ClipboardList,
  Lock,
  OctagonX,
  ShieldCheck,
} from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { toastApiError } from '@/shared/lib/api-error'
import { cn } from '@/shared/lib/utils'
import {
  buildFormFieldFromMeta,
  normaliseRegistrationField,
  useForm,
  type FormField,
} from '@/shared/lib/forms'
import { useUploadSubjectEvidence } from '@/admin/hooks/use-subject-attachments'
import {
  useSaveChecklistSection,
  type ChecklistSection,
  type ChecklistSectionHeld,
  type ChecklistSectionStatus,
  type InspectionChecklist,
  type SafetyHold,
  type VisitForm,
  visitFormKey,
} from '@/admin/hooks/use-inspections'
import { ChecklistItemExtras, EXTRAS, type ItemExtras } from './checklist-item-extras'
import { ChecklistRecord, RulesFound } from './inspection-checklist-record'

const SECTION_BADGE: Record<ChecklistSectionStatus, 'outline' | 'secondary' | 'default'> = {
  not_started: 'outline',
  in_progress: 'secondary',
  complete: 'default',
}

interface InspectionChecklistTabProps {
  inspectionUuid: string
  checklist: InspectionChecklist
  /** Which of the visit's forms this is; the checklist unless told. */
  form?: VisitForm
}

export function InspectionChecklistTab({
  inspectionUuid,
  checklist,
  form = 'checklist',
}: InspectionChecklistTabProps) {
  const { t } = useTranslation()
  const [openKey, setOpenKey] = useState<string | null>(null)
  // A save that found the section changed hands back the section as it now
  // is; the form opens again from that, whatever the checklist read shows yet.
  const [reread, setReread] = useState<{ n: number; section: ChecklistSectionHeld } | null>(
    null,
  )
  const [notice, setNotice] = useState<string | null>(null)
  const { template, sections, score } = checklist
  const done = sections.filter((section) => section.status === 'complete').length
  const label = new Map(checklist.questions.map((q) => [q.code_name, q.display_name]))
  const failed = (score?.failed_critical ?? []).map((code) => label.get(code) ?? code)
  // A checklist that locks while a section is open closes it.
  const open = checklist.locked ? undefined : sections.find((s) => s.key === openKey)
  const sectionLabel = useSectionLabels()
  const statusLabel: Record<InspectionChecklist['status'], string> = {
    none: '',
    not_started: t('inspections.checklist.notStarted', 'Not started'),
    in_progress: t('inspections.checklist.inProgress', 'In progress'),
    submitted: t('inspections.checklist.submitted', 'Submitted'),
  }
  const openSection = (key: string | null) => {
    setNotice(null)
    setReread(null)
    setOpenKey(key)
  }

  return (
    <div
      className="flex flex-col gap-4"
      data-testid={form === 'safety' ? 'inspection-safety-check' : 'inspection-checklist'}
    >
      <section className="flex flex-col gap-3 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[var(--ink-3)]">
              {form === 'safety' ? <ShieldCheck size={16} /> : <ClipboardList size={16} />}
            </span>
            <div>
              <h3 className="text-sm font-medium text-[var(--ink)]">{template?.name}</h3>
              <p className="text-[11px] text-[var(--ink-3)]">
                {t('inspections.checklist.version', 'Version {{version}}', {
                  version: template?.version,
                })}
                {' - '}
                {t('inspections.checklist.progress', '{{done}} of {{total}} sections complete', {
                  done,
                  total: sections.length,
                })}
              </p>
            </div>
          </div>
          <Badge variant={checklist.status === 'not_started' ? 'outline' : 'secondary'}>
            {statusLabel[checklist.status]}
          </Badge>
        </div>
        {score?.percent != null || score?.outcome ? (
          <div
            className="flex flex-wrap items-center gap-2 text-[12px] text-[var(--ink-2)]"
            data-testid="inspection-checklist-score"
          >
            {score.percent != null ? (
              <span>
                {t('inspections.checklist.score', 'Score {{percent}}%', {
                  percent: score.percent,
                })}
              </span>
            ) : null}
            {score.pass_mark != null ? (
              <span className="text-[var(--ink-3)]">
                {t('inspections.checklist.passMark', 'Pass mark {{percent}}%', {
                  percent: score.pass_mark,
                })}
              </span>
            ) : null}
            {/* With nothing answered there is nothing to pass or fail yet. */}
            {score.outcome && checklist.status !== 'not_started' ? (
              <Badge variant={score.outcome === 'fail' ? 'destructive' : 'secondary'}>
                {score.outcome === 'fail'
                  ? t('inspections.checklist.fails', 'Fails')
                  : t('inspections.checklist.passes', 'Passes')}
              </Badge>
            ) : null}
            {failed.length > 0 ? (
              <span>
                {t('inspections.checklist.failedCritical', 'Failed critical: {{items}}', {
                  items: failed.join(', '),
                })}
              </span>
            ) : null}
          </div>
        ) : null}
        {checklist.suggestion ? (
          <p
            className="text-[12px] text-[var(--ink-2)]"
            data-testid="inspection-checklist-suggestion"
          >
            {t('inspections.checklist.suggested', 'Suggested result: {{result}}', {
              result: checklist.suggestion.label,
            })}
          </p>
        ) : null}
        {checklist.locked ? (
          <p
            className="flex items-center gap-1.5 text-[12px] text-[var(--ink-2)]"
            data-testid="inspection-checklist-locked"
          >
            <Lock size={12} />
            {form === 'safety'
              ? t(
                  'inspections.safety.locked',
                  'The result is recorded, so this safety check is locked.',
                )
              : t(
                  'inspections.checklist.locked',
                  'The result is recorded, so this checklist is locked.',
                )}
          </p>
        ) : null}
      </section>

      {checklist.hold && !checklist.locked ? <SafetyHoldBanner hold={checklist.hold} /> : null}

      {open ? null : <ChecklistNotice message={notice} />}

      {!open && checklist.evaluation ? <RulesFound evaluation={checklist.evaluation} /> : null}

      {open ? (
        <ChecklistSectionForm
          key={`${open.key}:${reread?.n ?? 0}`}
          inspectionUuid={inspectionUuid}
          checklist={checklist}
          form={form}
          section={open}
          reread={reread?.section.key === open.key ? reread.section : undefined}
          notice={notice}
          onOpen={openSection}
          onNotice={setNotice}
          onConflict={(section) => setReread((r) => ({ n: (r?.n ?? 0) + 1, section }))}
        />
      ) : checklist.locked ? (
        <ChecklistRecord checklist={checklist} />
      ) : (
        <section className="rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] px-5 py-2">
          <h3 className="py-2 text-sm font-medium text-[var(--ink)]">
            {t('inspections.checklist.sections', 'Sections')}
          </h3>
          <ul className="divide-y divide-[var(--rule)]">
            {sections.map((section) => {
              const row = (
                <>
                  <SectionSummary section={section} fallbackTitle={template?.name} />
                  <span className="flex shrink-0 items-center gap-1">
                    <Badge variant={SECTION_BADGE[section.status]}>
                      {sectionLabel[section.status]}
                    </Badge>
                    <ChevronRight size={14} className="text-[var(--ink-3)]" />
                  </span>
                </>
              )
              return (
                <li key={section.key} data-testid={`inspection-checklist-section-${section.key}`}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => openSection(section.key)}
                    className="h-auto w-full items-start justify-between gap-3 rounded-none px-0 py-3 text-left font-normal whitespace-normal hover:bg-transparent"
                  >
                    {row}
                  </Button>
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </div>
  )
}

/** What holds the start, as the safety check reads now. */
function SafetyHoldBanner({ hold }: { hold: SafetyHold }) {
  const { t } = useTranslation()
  if (hold.stops_work.length > 0) {
    return (
      <section
        role={hold.required ? 'alert' : 'status'}
        className={cn(
          'flex flex-col gap-1.5 rounded-lg border px-4 py-3 text-[12px]',
          hold.required
            ? 'border-[var(--status-error-border)] bg-[var(--status-error-bg)] text-[var(--status-error)]'
            : 'border-[var(--status-warn)] bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
        )}
        data-testid="inspection-safety-hold"
      >
        <p className="flex items-center gap-1.5 font-medium">
          <OctagonX size={14} aria-hidden />
          {hold.required
            ? t(
                'inspections.safety.cannotStart',
                "The inspection can't start: an answer stops the work.",
              )
            : t('inspections.safety.stopsWork', 'An answer says to stop the work.')}
        </p>
        <ul className="flex flex-col gap-0.5 pl-5">
          {hold.stops_work.map((stop) => (
            <li key={`${stop.code}:${stop.answer}`}>
              {stop.question}: {stop.answer}
            </li>
          ))}
        </ul>
      </section>
    )
  }
  if (hold.holds_start) {
    return (
      <p
        role="status"
        className="rounded-lg border border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3 text-[12px] text-[var(--ink-2)]"
        data-testid="inspection-safety-hold"
      >
        {t(
          'inspections.safety.finishToStart',
          'Finish the safety check before the inspection starts.',
        )}
      </p>
    )
  }
  return hold.required ? (
    <p
      role="status"
      className="flex items-center gap-1.5 rounded-lg border border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] px-4 py-3 text-[12px] text-[var(--status-ok)]"
      data-testid="inspection-safety-hold"
    >
      <ShieldCheck size={14} aria-hidden />
      {t('inspections.safety.doneAndSafe', 'The safety check is done and safe.')}
    </p>
  ) : null
}

function ChecklistNotice({ message }: { message: string | null }) {
  return message ? (
    <p
      role="status"
      className="rounded-lg border border-[var(--status-error)] px-4 py-3 text-[12px] text-[var(--status-error)]"
      data-testid="inspection-checklist-notice"
    >
      {message}
    </p>
  ) : null
}

function useSectionLabels(): Record<ChecklistSectionStatus, string> {
  const { t } = useTranslation()
  return {
    not_started: t('inspections.checklist.notStarted', 'Not started'),
    in_progress: t('inspections.checklist.inProgress', 'In progress'),
    complete: t('inspections.checklist.complete', 'Complete'),
  }
}

function SectionSummary({
  section,
  fallbackTitle,
}: {
  section: ChecklistSection
  fallbackTitle?: string
}) {
  const { t } = useTranslation()
  return (
    <span className="flex min-w-0 flex-col">
      <span className="text-sm text-[var(--ink)]">{section.title || fallbackTitle}</span>
      {section.description ? (
        <span className="text-[11px] text-[var(--ink-3)]">{section.description}</span>
      ) : null}
      {section.missing.length > 0 ? (
        <span className="text-[11px] text-[var(--ink-3)]">
          {t('inspections.checklist.toAnswer', { count: section.missing.length })}
        </span>
      ) : null}
    </span>
  )
}

// Refusals about an item's note or photos show by the note and photos.
/** A file answer still uploading holds the file itself, not its upload. */
const stillUploading = (value: unknown): boolean =>
  value instanceof File || (Array.isArray(value) && value.some((item) => item instanceof File))

const EXTRAS_ERRORS = new Set([
  'checklist.note_required',
  'checklist.photo_required',
  'checklist.invalid_note',
  'checklist.invalid_photo',
])

interface SaveRefusal {
  response?: {
    status?: number
    data?: {
      message?: string
      result?: {
        errors?: Record<string, { code?: string; message?: string }>
        section?: ChecklistSectionHeld
      }
    }
  }
}

interface ChecklistSectionFormProps {
  inspectionUuid: string
  checklist: InspectionChecklist
  form: VisitForm
  section: ChecklistSection
  /** The section as a refused save found it, to open from instead. */
  reread?: ChecklistSectionHeld
  notice: string | null
  onOpen: (key: string | null) => void
  onNotice: (message: string | null) => void
  onConflict: (section: ChecklistSectionHeld) => void
}

/** One section as a form; every move away from it saves it first. */
function ChecklistSectionForm({
  inspectionUuid,
  checklist,
  form,
  section,
  reread,
  notice,
  onOpen,
  onNotice,
  onConflict,
}: ChecklistSectionFormProps) {
  const { t } = useTranslation()
  const top = useRef<HTMLElement>(null)
  // A section opens at its top, so a phone starts on its first item; one
  // reopened after a refused save stays where it was.
  useEffect(() => {
    if (!reread) top.current?.scrollIntoView({ block: 'start' })
  }, [reread])
  const save = useSaveChecklistSection(inspectionUuid, form)
  const queryClient = useQueryClient()
  const upload = useUploadSubjectEvidence('inspection', inspectionUuid, 'INSPECTION_EVIDENCE')
  const sectionLabel = useSectionLabels()
  // Photos still uploading: saving waits for them, so none is left behind.
  const [uploading, setUploading] = useState(0)
  const busy = save.isPending || uploading > 0
  const { sections, questions, answers, notes, photos } = checklist
  const at = sections.findIndex((s) => s.key === section.key)
  const codes = questions.filter((q) => q.section === section.key).map((q) => q.code_name)

  // Every question is in the form, so a show-when rule can read an answer
  // from another section; only this section's show. Pick-one and
  // multi-select answers show as chips, with their flags, and each item
  // takes a note and photos under its answer.
  const fields = useMemo(
    () =>
      questions.flatMap((question, order): FormField[] => {
        const code = question.code_name
        const shown = question.section === section.key
        const source = reread && shown ? reread : { answers, notes, photos }
        const answer = buildFormFieldFromMeta(
          normaliseRegistrationField({
            ...question,
            options: question.options as never,
            validation_rules: question.validation_rules as never,
            order,
          }),
          source.answers[code],
          'submit',
        )
        const visibleWhen = question.configuration_data?.visible_when
        return [
          { ...answer, chips: true, visible: shown },
          {
            type: 'custom',
            name: `${code}${EXTRAS}`,
            value: { note: source.notes[code] ?? '', photos: source.photos[code] ?? [] },
            visible: shown,
            configurationData: visibleWhen ? { visible_when: visibleWhen } : undefined,
            render: () => <ChecklistItemExtras question={question} onUploading={setUploading} />,
          },
        ]
      }),
    [questions, answers, notes, photos, section.key, reread],
  )

  const { Form, ref } = useForm(
    {
      name: `inspection-checklist-${section.key}`,
      submit: () => <></>,
      fields,
      // A file answer or an item's photo is uploaded to the inspection,
      // labelled with its question.
      uploadFile: async ({ file, fieldName = '' }) => {
        const code = fieldName.endsWith(EXTRAS) ? fieldName.slice(0, -EXTRAS.length) : fieldName
        const uploaded = await upload.mutateAsync({
          file,
          category: questions.find((q) => q.code_name === code)?.display_name ?? '',
        })
        return {
          uuid: uploaded.uuid,
          file_name: uploaded.file_name,
          file_url: uploaded.file_url,
          mime_type: uploaded.mime_type,
        }
      },
    },
    async () => undefined,
    // The note and photos render reads its question.
    [questions],
  )

  // The version this form was read at, and what it held then: a save sends
  // only what changed since, and is refused if the section moved meanwhile.
  const read = useRef(reread?.version ?? section.version)
  const held = useRef<Record<string, unknown> | null>(null)
  useEffect(() => {
    if (!held.current && ref.current) held.current = { ...ref.current.values }
  })

  const refused = (error: unknown) => {
    const response = (error as SaveRefusal).response
    if (response?.status === 409) {
      onNotice(
        t(
          'inspections.checklist.conflict',
          'Someone else saved this section while you had it open. It now shows their answers, so make your changes again.',
        ),
      )
      const current = response.data?.result?.section
      if (current) onConflict(current)
    } else if (response?.status === 422) {
      const errors = Object.entries(response.data?.result?.errors ?? {})
      const onFields = errors.filter(([code]) => codes.includes(code))
      for (const [code, problem] of onFields) {
        const extras = EXTRAS_ERRORS.has(problem.code ?? '')
        ref.current?.setFieldError(extras ? `${code}${EXTRAS}` : code, problem.message)
      }
      onNotice(
        onFields.length > 0
          ? t('inspections.checklist.fixAnswers', 'Some answers need a change before this section saves.')
          : (response.data?.message ?? null),
      )
    } else if (response?.status === 423) {
      // The checklist reads as locked once it is fetched again, which closes this form.
      onNotice(
        form === 'safety'
          ? t(
              'inspections.safety.lockedWhileOpen',
              'The result was recorded, so this safety check is locked and the section was not saved.',
            )
          : t(
              'inspections.checklist.lockedWhileOpen',
              'The result was recorded, so this checklist is locked and the section was not saved.',
            ),
      )
    } else {
      toastApiError(error, t('inspections.checklist.saveFailed', 'This section could not be saved.'))
    }
  }

  /** Save what changed; true when the section holds what the form shows. */
  const persist = async (): Promise<boolean> => {
    const form = ref.current
    if (!form) return false
    const values = { ...form.values }
    const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
    const sent = {
      answers: {} as Record<string, unknown>,
      notes: {} as Record<string, string>,
      photos: {} as Record<string, string[]>,
    }
    for (const code of codes) {
      if (!same(values[code], held.current?.[code])) sent.answers[code] = values[code] ?? null
      const now = values[`${code}${EXTRAS}`] as ItemExtras | undefined
      const was = held.current?.[`${code}${EXTRAS}`] as ItemExtras | undefined
      if ((now?.note ?? '') !== (was?.note ?? '')) sent.notes[code] = now?.note ?? ''
      const uuids = (now?.photos ?? []).map((photo) => photo.uuid)
      if (!same(uuids, (was?.photos ?? []).map((photo) => photo.uuid))) sent.photos[code] = uuids
    }
    if (Object.values(sent).every((part) => Object.keys(part).length === 0)) return true
    // Saving now would lose a file that has not finished uploading.
    if (Object.values(sent.answers).some(stillUploading)) {
      onNotice(
        t(
          'inspections.checklist.fileUploading',
          'A file is still uploading. Save again once it has finished.',
        ),
      )
      return false
    }
    onNotice(null)
    try {
      const saved = await save.mutateAsync({
        key: section.key,
        version: read.current,
        idempotencyKey: crypto.randomUUID(),
        ...sent,
      })
      read.current = saved.section.version
      held.current = values
      return true
    } catch (error) {
      refused(error)
      return false
    }
  }
  /** Save, then open the previous or next section, or the list. */
  const move = async (step: -1 | 1 | null) => {
    if (!(await persist())) return
    if (step === null) return onOpen(null)
    // A save can show or hide sections (a show-when rule), so step through
    // them as the form reads now.
    const now =
      queryClient.getQueryData<InspectionChecklist>(visitFormKey(inspectionUuid, form))
        ?.sections ?? sections
    // A section the save hid has no place to step from: back to the list.
    const index = now.findIndex((s) => s.key === section.key)
    onOpen(index === -1 ? null : (now[index + step]?.key ?? null))
  }
  const previous = sections[at - 1]
  const next = sections[at + 1]

  return (
    <section
      ref={top}
      className="flex scroll-mt-4 flex-col gap-4 rounded-lg border border-[var(--rule)] bg-[var(--bg-card)] p-5"
      data-testid="inspection-checklist-form"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {t('upload.form.sectionOf', 'Section {{current}} of {{total}}', {
              current: at + 1,
              total: sections.length,
            })}
          </p>
          <SectionSummary section={section} fallbackTitle={checklist.template?.name} />
        </div>
        <Badge variant={SECTION_BADGE[section.status]} className="shrink-0">
          {sectionLabel[section.status]}
        </Badge>
      </div>
      <ChecklistNotice message={notice} />
      <Form className="flex flex-col gap-3" />
      {/* Stays in reach at the foot of the screen while a long section scrolls. */}
      <div className="sticky bottom-0 z-10 -mx-5 -mb-5 flex items-center gap-2 rounded-b-lg border-t border-[var(--rule)] bg-[var(--bg-card)] px-5 py-3">
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          onClick={() => void move(null)}
          aria-label={t('inspections.checklist.allSections', 'All sections')}
          data-testid="inspection-checklist-all-sections"
        >
          <ArrowLeft size={14} />
          <span className="hidden sm:inline">
            {t('inspections.checklist.allSections', 'All sections')}
          </span>
        </Button>
        <div className="ml-auto flex items-center gap-2">
          {previous ? (
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => void move(-1)}
              data-testid="inspection-checklist-previous"
            >
              {t('common.previous', 'Previous')}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={async () => {
              if (await persist()) toast.success(t('inspections.checklist.saved', 'Section saved'))
            }}
            data-testid="inspection-checklist-save"
          >
            {t('common.save', 'Save')}
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={() => void move(1)}
            data-testid="inspection-checklist-next"
          >
            {next ? (
              <>
                {t('common.next', 'Next')}
                <ArrowRight size={14} />
              </>
            ) : (
              t('inspections.checklist.done', 'Done')
            )}
          </Button>
        </div>
      </div>
    </section>
  )
}
